import { Injectable, signal, effect, inject, OnDestroy } from '@angular/core'
import isEqual from 'fast-deep-equal'
import { Subject, Subscription } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'

import { Hunt } from '@cyborg-monorepo/hunter/common'

import { AuthService } from '../../../auth/auth.service'

import { HuntDatabaseService } from './db/hunt-database.service'
import { HydrationService } from './hydration.service'
import {
  clearHuntDraft,
  readHuntDraft,
  shouldOfferDraftRecovery,
  writeHuntDraft,
} from './utils/hunt-draft-store.utils'
import { retryWithBackoff } from './utils/retry-with-backoff.utils'

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'failed'

const MAX_SAVE_ATTEMPTS = 3
const RETRY_BASE_DELAY_MS = 1000
const SAVED_STATUS_DISPLAY_MS = 2500
const SHOW_SAVING_DELAY_MS = 300

@Injectable({ providedIn: 'root' })
export class SessionService implements OnDestroy {
  private readonly hydration = inject(HydrationService)
  private readonly db = inject(HuntDatabaseService)
  private readonly authService = inject(AuthService)

  private readonly autosave$ = new Subject<Hunt>()
  private readonly autosaveSub: Subscription

  /**
   * The most recent hunt snapshot that has not yet been *confirmed* saved.
   * Stays populated across failed attempts so nothing is lost -- it is
   * only cleared once the server confirms the matching snapshot was
   * persisted.
   */
  private pendingSnapshot: Hunt | null = null

  /**
   * Tags each submitSnapshot() attempt so a stale, overlapping save can
   * tell it's no longer authoritative and skip mutating shared state.
   * Overlap is expected: retryWithBackoff can keep an older attempt in
   * flight for several seconds while a newer debounced edit starts its
   * own attempt.
   */
  private latestAttemptId = 0

  /** Snapshot as first loaded from DB/API */
  readonly originalHunt = signal<Hunt | null>(null)

  /** Currently active hunt */
  readonly activeHunt = signal<Hunt | null>(null)

  /** True while loading/submitting */
  readonly loading = signal(false)

  /** True if activeHunt could possibly have been modified by consumers outside of hunt workbench like hunt list or hunt template */
  readonly isDirty = signal(false)

  /**
   * Status of the most recent autosave attempt. The hunt editor UI should
   * surface `'failed'` prominently (e.g. "not saved") since it means the
   * user's edits are not yet safely persisted server-side.
   */
  readonly saveStatus = signal<SaveStatus>('idle')

  /**
   * A locally-backed-up draft that differs from what the server returned
   * for the currently loaded hunt (i.e. a prior session may have failed to
   * save). Never auto-applied -- the UI should prompt the user to recover
   * or dismiss it via `recoverDraft()` / `dismissDraft()`.
   */
  readonly recoverableDraft = signal<Hunt | null>(null)

  private readonly onBeforeUnload = () => this.flushOnUnload()

  constructor() {
    effect(() => {
      const id = this.hydration.param('id')()

      // No id → clear session
      if (!id) {
        this.originalHunt.set(null)
        this.activeHunt.set(null)
        this.loading.set(false)
        return
      }

      // Load new hunt when id changes
      this.loadHunt(id)
    })

    // effect to push changes into autosave$
    effect(() => {
      const hunt = this.activeHunt()
      if (!hunt) return
      this.pendingSnapshot = hunt
      this.autosave$.next(hunt)
    })

    // debounced autosave pipeline
    this.autosaveSub = this.autosave$
      .pipe(
        debounceTime(2000),
        distinctUntilChanged((a, b) => a.id === b.id && isEqual(a, b)),
      )
      .subscribe((hunt) => {
        this.submitSnapshot(hunt)
      })

    window.addEventListener('beforeunload', this.onBeforeUnload)
  }

  ngOnDestroy(): void {
    this.flushNow()
    this.autosaveSub.unsubscribe()
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  }

  /**
   * Immediately submit any pending autosave snapshot, bypassing the
   * debounce timer. Used for manual "Save" actions and on component
   * teardown. Retries with backoff like the debounced path.
   */
  flushNow(): void {
    const hunt = this.pendingSnapshot
    if (!hunt) return

    this.submitSnapshot(hunt)
  }

  /**
   * Best-effort flush for the `beforeunload` path. There's no time to await
   * retries once the page is unloading, so this fires a single keepalive
   * request (guaranteed by the browser to complete even after the page
   * navigates away) rather than going through the normal retry pipeline.
   * The local backup write ensures a `recover unsaved changes?` prompt is
   * available next time the hunt is opened even if the beacon fails.
   */
  private flushOnUnload(): void {
    const hunt = this.pendingSnapshot
    if (!hunt) return

    this.writeLocalBackup(hunt)
    this.db.submitHuntBeacon(hunt)
  }

  private async submitSnapshot(hunt: Hunt): Promise<void> {
    if (this.originalHunt() && isEqual(this.originalHunt(), hunt)) {
      this.clearPendingSnapshot(hunt)
      return
    }

    const attemptId = ++this.latestAttemptId
    const isLatestAttempt = () => attemptId === this.latestAttemptId

    // Guards saveStatus/originalHunt updates against a hunt switch that
    // happens while this save is still in flight -- a late-arriving result
    // for a hunt that's no longer on screen shouldn't overwrite the new
    // hunt's status or baseline.
    const isCurrentHunt = () => this.activeHunt()?.id === hunt.id
    const canUpdateSessionState = () => isLatestAttempt() && isCurrentHunt()

    // Only show "Saving..." once the request has actually taken a moment --
    // a fast save should go straight to "Saved" rather than flashing an
    // intermediate state nobody has time to read.
    const showSavingTimer = setTimeout(() => {
      if (canUpdateSessionState()) this.saveStatus.set('saving')
    }, SHOW_SAVING_DELAY_MS)

    this.writeLocalBackup(hunt)

    try {
      const saved = await retryWithBackoff(
        async () => {
          const result = await this.db.submitHunt(hunt)
          if (!result) {
            throw new Error('Hunt save returned no result')
          }
          return result
        },
        {
          maxAttempts: MAX_SAVE_ATTEMPTS,
          baseDelayMs: RETRY_BASE_DELAY_MS,
          onAttemptFailed: (err) =>
            console.error('SessionService.autosave', err),
        },
      )

      clearTimeout(showSavingTimer)

      if (canUpdateSessionState()) {
        this.originalHunt.set(saved)
      }
      // Only update activeHunt if the user hasn't made further edits
      // while the request was in flight
      if (isEqual(this.activeHunt(), hunt)) {
        this.activeHunt.set(saved)
      }

      this.clearPendingSnapshot(hunt)
      // Scoped to this hunt's own id rather than the currently active hunt
      // -- safe to clear even after navigating away, as long as no newer
      // attempt for this same hunt is still in flight to depend on it.
      if (isLatestAttempt()) {
        this.clearLocalBackup(hunt.id)
      }
      if (canUpdateSessionState()) {
        this.saveStatus.set('saved')
        // Let the confirmation sit for a moment instead of instantly
        // vanishing -- back to idle unless a newer save/hunt switch has
        // already moved saveStatus on by the time this fires.
        setTimeout(() => {
          if (canUpdateSessionState() && this.saveStatus() === 'saved') {
            this.saveStatus.set('idle')
          }
        }, SAVED_STATUS_DISPLAY_MS)
      }
    } catch (err) {
      clearTimeout(showSavingTimer)
      console.error('SessionService.autosave', err)

      if (canUpdateSessionState()) {
        // Preserve the snapshot -- a later edit or the next autosave cycle
        // can still pick it up and retry. Nothing is dropped. Skipped if a
        // newer attempt (or a hunt switch) has already superseded this
        // one, so a slow failing retry can't stomp a pending edit that's
        // already ahead of it.
        this.pendingSnapshot = hunt
        this.saveStatus.set('failed')
      }
    }
  }

  /** Clear pendingSnapshot only if no newer edit has superseded it */
  private clearPendingSnapshot(hunt: Hunt): void {
    if (this.pendingSnapshot && isEqual(this.pendingSnapshot, hunt)) {
      this.pendingSnapshot = null
    }
  }

  /**
   * Drafts are scoped by user id (not just hunt id) since localStorage
   * persists across logins in the same browser -- without this, one user's
   * failed-save draft could surface as a recovery prompt for a different
   * user who later opens the same hunt on that machine.
   */
  private currentUserId(): number | null {
    return this.authService.user$.value?.id ?? null
  }

  private writeLocalBackup(hunt: Hunt): void {
    const userId = this.currentUserId()
    if (userId === null) return
    writeHuntDraft(hunt, userId, localStorage)
  }

  private clearLocalBackup(id: string): void {
    const userId = this.currentUserId()
    if (userId === null) return
    clearHuntDraft(userId, id, localStorage)
  }

  private readLocalBackup(id: string): Hunt | null {
    const userId = this.currentUserId()
    if (userId === null) return null
    return readHuntDraft(userId, id, localStorage)
  }

  /**
   * Apply a previously-detected local draft as the active hunt so it can be
   * reviewed and re-saved. Does not touch the server directly -- the next
   * autosave cycle or manual save will persist it.
   */
  public recoverDraft(): void {
    const draft = this.recoverableDraft()
    if (!draft) return

    this.activeHunt.set(draft)
    // Explicitly schedule the recovered content for saving rather than
    // relying on the activeHunt-change effect to do it -- that effect runs
    // async, so a flushNow() called synchronously right after (e.g. on
    // immediate navigation away) could otherwise see a stale null
    // pendingSnapshot and no-op, leaving the recovered draft unpersisted.
    this.pendingSnapshot = draft
    this.autosave$.next(draft)
    this.recoverableDraft.set(null)
  }

  /** Dismiss a detected local draft without applying it. */
  public dismissDraft(): void {
    const id = this.activeHunt()?.id ?? this.originalHunt()?.id
    this.recoverableDraft.set(null)
    if (id) this.clearLocalBackup(id)
  }

  /** Mark the active hunt as dirty */
  public markDirty(): void {
    this.isDirty.set(true)
  }

  /**
   * Load a hunt into the session.
   */
  async loadHunt(id: string, fetch = false): Promise<Hunt | undefined> {
    if (!fetch && !this.isDirty()) {
      if (this.activeHunt()?.id === id) {
        return this.activeHunt() ?? undefined
      }
    }

    this.loading.set(true)
    this.isDirty.set(false)
    // A failed/in-progress save belongs to the hunt that was active when it
    // happened -- don't let it linger and misrepresent the hunt we're about
    // to show.
    this.saveStatus.set('idle')

    try {
      const hunt = await this.db.getHunt(id)
      this.originalHunt.set(hunt ?? null)
      this.activeHunt.set(hunt ?? null)

      // A local backup that diverges from the server's copy means a prior
      // save attempt may have failed silently -- surface it for the user
      // to decide on, never auto-apply it (see HUN-1091).
      const backup = this.readLocalBackup(id)
      if (backup && hunt && isEqual(backup, hunt)) {
        // Confirmed stale: the backup already matches the server, so
        // there's nothing to recover.
        this.recoverableDraft.set(null)
        this.clearLocalBackup(id)
      } else if (backup && (shouldOfferDraftRecovery(hunt, backup) || !hunt)) {
        // Either the backup provably diverges from the server's copy, or
        // `hunt` is null because the fetch itself failed (e.g. offline) and
        // we simply couldn't compare -- offer it either way rather than
        // silently hiding it just because we can't prove it's stale.
        this.recoverableDraft.set(backup)
      } else {
        this.recoverableDraft.set(null)
      }

      return hunt
    } catch (err) {
      console.error('SessionService.loadHunt', err)
      this.originalHunt.set(null)
      this.activeHunt.set(null)
      return undefined
    } finally {
      this.loading.set(false)
    }
  }

  public clearActiveHunt(): void {
    this.originalHunt.set(null)
    this.activeHunt.set(null)
  }
}
